<template lang="pug">
.feedback-listing
  template(v-if="isFetching")
    v-row(no-gutters)
      v-col(cols="8")
        v-skeleton-loader(type="article@3")

  template(v-else)
    template(v-if="totalCount === 0")
      listing-empty-state.my-4(v-if="hasFiltersOrSearch" iconName="$yzi-star-01" :primaryText="$t('item_not_found', { item:'feedback' })" :secondaryText="$t('item_not_found_description')")
      listing-empty-state.my-4(v-else iconName="$yzi-star-01" :primaryText="$t('feedback_listing_empty_primary_text')" :secondaryText="$t('feedback_listing_empty_secondary_text')" :btnText="$t('send_review_invite')" @btnClicked="$emit('toInvitationTab')")

    v-row(v-else)
      v-col(cols="12" md="5")
        .d-flex.align-center.mt-1
          v-menu(offset-y rounded="lg")
            template(#activator="{ on, attrs }")
              v-chip.height-28.text-xs.pl-3.pr-2(outlined v-on="on")
                span.mr-1 {{ selectedDuration.text }}
                v-icon(small) $yzi-chevron-down
            v-list.py-0
              template(v-for="duration in durations")
                v-list-item(@click="selectDuration(duration)")
                  v-list-item-title.text-sm {{ duration.text }}
                v-divider.neutral300
          v-spacer
          v-menu(offset-y rounded="lg" left)
            template(#activator="{ on, attrs }")
              v-btn(small :text="!!selectedSort" :icon="!selectedSort" :color="selectedSort ? 'primary' : 'neutral500'" v-on="on" :class="selectedSort ? 'selected-button' : ''")
                template(v-if="selectedSort === 'new'")
                  v-icon(size="20") $yzi-menu-03
                  v-icon(size="14") $yzi-arrow-up
                template(v-else-if="selectedSort === 'old'")
                  v-icon(size="20") $yzi-menu-03
                  v-icon(size="14") $yzi-arrow-down
                template(v-else)
                  v-icon(size="20") $yzi-filter-line
            v-list.py-0
              .px-4.pt-3.pb-2
                .font-semibold.text-sm {{ $t('sort') }}
              template(v-for="option in sortOptions")
                v-list-item(@click="sortFeedback(option)")
                  v-list-item-title.text-sm {{ option.text }}
                v-divider.neutral300
        
        v-data-table.mt-2.feedback-table(:headers="headers" hide-default-footer :header-props="{ sortIcon: '$yzi-triangle' }" :items="reviews" :items-per-page="reviews.length" :loading="isLoading" loader-height="2" :disable-sort="isMobileScreen" :mobile-breakpoint="0")
          template(#header.lead.name)
            feedback-icon.mr-2(icon-name="$yzi-menu-05")
            span.text-xs.font-normal {{ $t('name') }}
          template(#header.created)
            feedback-icon.mr-2(icon-name="$yzi-alarm-clock")
            span.text-xs.font-normal {{ $t('submitted_at') }}
          template(#item="{ item, isMobile, headers, index }")
            tr.cursor-pointer(:class="(selectedReview && (item.id === selectedReview.id)) ? 'selected-row' : ''" @click="selectReview(item)")
              td.py-3
                .text-sm {{ item.name }}
              td.py-3
                .text-xs.font-bold {{ item.created | convertToDateOnly }}
                .text-xs {{ item.created | convertToTimeOnly }}
          template(#footer)
            v-card(height="1px" v-intersect="infiniteScrolling" flat)
            .text-center.py-3(v-if="isLoadMore && hasNext")
              v-progress-circular(width="1.5" size="16" color="grey" indeterminate)
        
        .download-button
          v-divider
          .py-2.white
          v-btn.button-sm(@click="downloadAllResponses" block color="primary" depressed :loading="isDownloading") {{ $t('download_all_responses') }}

      v-col(v-if="!isMobileScreen" cols="7")
        listing-feedback-item.my-2.feedback-column(v-if="selectedReview" responseType="feedback" :name="selectedReview.lead.name" :dateTime="convertDateTime(selectedReview.created)" :star="selectedReview.star" :comment="selectedReview.comment" :response="selectedReview.internal_notes" :extra_fields_data="selectedReview.extra_fields_data" :labels="selectedReview.labels" :rightBtnText="$t('add_internal_notes')" :middleBtnText="$t('assign_team')" :leftBtnText="getLeftBtnText(selectedReview)" @leftBtnClicked="viewCustomer(selectedReview)" :rightBtnDisabled="selectedReview.internal_notes !== null" :middleBtnDisabled="selectedReview.labels !== null && selectedReview.labels.length > 0" :isEditingResponse="isShowingBottomView" :overrideBottomView="isShowingBottomView" @middleBtnClicked="assignTeam" @rightBtnClicked="editResponse(selectedReview)" @deleteResponse="openDeleteResponseDialog = true" @editResponse="editResponse(selectedReview)" @deleteAssignee="openDeleteAssigneeDialog = true" @editAssignee="assignTeam")
          template(#itemAction)
            v-btn(v-clipboard:copy="getReviewText()" v-clipboard:success="doneCopy" icon small)
              v-icon(size="20") $yzi-copy-02
          template(#bottomView)
            template(v-if="isShowingBottomView")
              assign-item(v-if="isEditingAssignee" :currentAssignees="selectedReview.labels" :rightBtnLoading="isSavingAssignee" @rightBtnClicked="createAssignee" @leftBtnClicked="resetData" :items="assignees")
              reply-item(v-else :title="$t('internal_notes')" :placeholder="$t('your_notes')" :currentReply="selectedReview.internal_notes" :rightBtnText="$t('save')" :rightBtnLoading="isSavingResponse" :leftBtnText="$t('cancel')" @leftBtnClicked="resetData" @rightBtnClicked="saveResponse" replyType="feedback")
              
  .dialogs
    delete-confirmation-dialog(v-if="openDeleteResponseDialog" :open.sync="openDeleteResponseDialog" :isLoading="isDeletingResponse" @cancel="resetData(); openDeleteResponseDialog = false" @delete="deleteResponse")
      .text-lg.font-medium {{ $t('delete_confirmation', { text: $t('internal_notes').toLowerCase() }) }}
      .text-md.mt-4 {{ $t('review_delete_confirmation_description_primary', { text: $t('internal_notes') }) }}             
      .text-md {{ $t('review_delete_confirmation_description_secondary') }}
    delete-confirmation-dialog(v-if="openDeleteAssigneeDialog" :open.sync="openDeleteAssigneeDialog" :isLoading="isDeletingAssignee" @cancel="resetData(); openDeleteAssigneeDialog = false" @delete="deleteAssignee")
      .text-lg.font-medium {{ $t('delete_confirmation', { text: $t('assignees').toLowerCase() }) }}
      .text-md.mt-4 {{ $t('review_delete_confirmation_description_primary', { text: $t('assignees') }) }}             
      .text-md {{ $t('review_delete_confirmation_description_secondary') }}
    
    y-dialog(v-if="selectedReview" v-model="openSelectedDialog" persistent :fullscreen="isMobileScreen" max-width="600px" scrollable transition="fade-transition")
      v-card
        v-card-title
          .d-flex.align-center
            v-btn.mr-3(icon @click="hideSelectedDialog" color="primary" width="20" height="20") 
              v-icon(size="20") $yzi-arrow-narrow-left
            .text-2xl.font-semibold {{ this.selectedReview.name }}
        v-card-text
          listing-feedback-item.my-2(v-if="selectedReview" responseType="feedback" :name="selectedReview.lead.name" :dateTime="convertDateTime(selectedReview.created)" :star="selectedReview.star" :comment="selectedReview.comment" :response="selectedReview.internal_notes" :extra_fields_data="selectedReview.extra_fields_data" :labels="selectedReview.labels" :rightBtnText="$t('add_internal_notes')" :middleBtnText="$t('assign_team')" :leftBtnText="getLeftBtnText(selectedReview)" @leftBtnClicked="viewCustomer(selectedReview)" :rightBtnDisabled="selectedReview.internal_notes !== null" :leftBtnDisabled="selectedReview.labels !== null && selectedReview.labels.length > 0" :isEditingResponse="isShowingBottomView" :overrideBottomView="isShowingBottomView" @middleBtnClicked="assignTeam" @rightBtnClicked="editResponse(selectedReview)" @deleteResponse="openDeleteResponseDialog = true" @editResponse="editResponse(selectedReview)" @deleteAssignee="openDeleteAssigneeDialog = true" @editAssignee="assignTeam")
            template(#itemAction)
              v-btn(v-clipboard:copy="getReviewText()" v-clipboard:success="doneCopy" icon small)
                v-icon(size="20") $yzi-copy-02
            template(#bottomView)
              template(v-if="isShowingBottomView")
                assign-item(v-if="isEditingAssignee" :currentAssignees="selectedReview.labels" :rightBtnLoading="isSavingAssignee" @rightBtnClicked="createAssignee" @leftBtnClicked="resetData" :items="assignees")
                reply-item(v-else :title="$t('internal_notes')" :placeholder="$t('your_notes')" :currentReply="selectedReview.internal_notes" :rightBtnText="$t('save')" :rightBtnLoading="isSavingResponse" :leftBtnText="$t('cancel')" @leftBtnClicked="resetData" @rightBtnClicked="saveResponse" replyType="feedback")
</template>

<script>
import { DateTime } from 'luxon'
import { mapGetters, mapActions } from 'vuex'
import ListingEmptyState from '@/components/review/listing/ListingEmptyState.vue'
import ListingFeedbackItem from '@/components/review/listing/ListingFeedbackItem.vue'
import ReplyItem from '@/components/review/listing/ReplyItem.vue'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'
import AssignItem from '@/components/review/listing/AssignItem.vue'
import FeedbackIcon from '../general/FeedbackIcon.vue'

export default {
  name: 'FeedbackListing',
  components: {
    ListingEmptyState,
    ListingFeedbackItem,
    ReplyItem,
    DeleteConfirmationDialog,
    AssignItem,
    FeedbackIcon
  },
  props: {
    searchText: {
      type: String,
      default: ''
    },
    filters: {
      type: Array,
      default: () => {
        return []
      }
    },
    os: {
      type: String,
      default: 'review',
      validator(value) {
        return ['clinic', 'donation', 'connect', 'review'].includes(value)
      }
    },
  },
  data() {
    return {
      isFetching: false,
      isLoading: false,
      isLoadMore: false,
      totalCount: 0,
      pageSize: 20,
      currentPage: 1,
      hasNext: false,
      reviews: [],
      selectedReview: null,
      isShowingBottomView: false,
      isSavingResponse: false,
      isDeletingResponse: false,
      isEditingAssignee: false,
      isSavingAssignee: false,
      isDeletingAssignee: false,
      openDeleteResponseDialog: false,
      openDeleteAssigneeDialog: false,
      selectedDuration: { text: 'All time', value: null },
      durations: [
        { text: 'All time', value: null },
        { text: 'Past 1 month', value: 1 },
        { text: 'Past 3 months', value: 3 },
        { text: 'Past 6 months', value: 6 },
        { text: 'Past 12 months', value: 12 },
      ],
      selectedSort: '',
      sortOptions: [
        { text: 'Newest to oldest', value: 'new' },
        { text: 'Oldest to newest', value: 'old' }
      ],
      headers: [
        { text: 'Name', value: 'lead.name'},
        { text: 'Submitted at', value: 'created' }
      ],
      assignees: [],
      openSelectedDialog: false,
      isDownloading: false
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      reviewAccount: 'review/getMainAccount'
    }),
    hasFiltersOrSearch() {
      return this.filters.length > 0 || this.searchText.length > 0
    },
    isMobileScreen () {
      return this.$vuetify.breakpoint.sm || this.$vuetify.breakpoint.xs
    },
  },
  async mounted () {
    this.isFetching = true
    await this.fetchFeedback()
    this.fetchAssignees()
    this.isFetching = false
  },
  watch: {
    filters() {
      this.currentPage = 1
      this.fetchFeedback()
    },
  },
  methods: {
    ...mapActions(['showMessage']),
    convertDateTime(value) {
      const isoDateTime = DateTime.fromISO(value)
      const date = isoDateTime.toFormat('dd MMM yyyy')
      const time = isoDateTime.toFormat('hh:mm a')
      return `${date}, ${time}`
    },
    async fetchFeedback() {
      try {
        this.isLoading = true
        const params = {
          expand: 'lead',
          search: this.searchText,
          page_size: this.pageSize,
          page: this.currentPage,
        }
        if (this.filters.length > 0) {
          this.filters.map(filter => {
            if (filter.value) {
              params[filter.key] = filter.value
            }
          })
        }
        if (this.selectedSort) {
          params['ordering'] = this.selectedSort === 'new' ? '-created' : 'created'
        }
        if (this.selectedDuration.value) {
          params['past_months'] = this.selectedDuration.value
        }
        const { data } = await this.$axios.get(`/reviews/${this.company.slug}/reviews`, { params })
        this.totalCount = data.count
        const firstFetch = this.currentPage === 1
        this.reviews = firstFetch ? data.results : this.reviews.concat(data.results)
        this.hasNext = data.next !== null
        if (firstFetch && !this.isMobileScreen) {
          this.selectedReview = this.reviews.length > 0 ? this.reviews[0] : null
        }
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isLoading = false
      } finally {
        this.isLoading = false
        this.isLoadMore = false
      }
    },
    editResponse() {
      this.isEditingAssignee = false
      this.isShowingBottomView = true
    },
    async saveResponse(value) {
      this.isSavingResponse = true
      try {
        const { data } = await this.$axios.patch(`/reviews/${this.company.slug}/reviews/${this.selectedReview.lead.pid}?pid=true&expand=lead`, { internal_notes: value })
        this.updateReview(data)
        this.showMessage(['success', this.$t('success_message.successfully_updated')])
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.resetData()
      }
    },
    async deleteResponse() {
      this.isDeletingResponse = true
      try {
        const { data } = await this.$axios.patch(`/reviews/${this.company.slug}/reviews/${this.selectedReview.lead.pid}?pid=true&expand=lead`, { internal_notes: null })
        this.updateReview(data)
        this.showMessage(['success', this.$t('success_message.delete_successful')])
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.openDeleteResponseDialog = false
        this.resetData()
      }
    },
    fetchAssignees() {
      this.$axios.get(`/options?${this.company.id}&field=review.label`)
        .then(resp => {
          this.assignees = resp.data.map(item => item.value)
        })
        .catch(() => {})
    },
    async createAssignee(value) {
      this.isSavingAssignee = true
      const filteredAssignee = value.filter(item => !this.assignees.includes(item))
      if (filteredAssignee && filteredAssignee.length > 0) {
        await this.asyncForEach(filteredAssignee, async assignee => {
          try {
            const params = {
              company: this.company.id,
              field: 'review.label',
              value: assignee
            }
            const resp = await this.$axios.post('options', params)
            const item = resp.data.value
            if (item) {
              this.assignees.push(item)
            }
          } catch (e) {
            console.log(e)
          }
        })
      }
      this.isSavingAssignee = false
      this.saveAssignee(value)
    },
    async saveAssignee(value) {
      this.isSavingAssignee = true
      try {
        const { data } = await this.$axios.patch(`/reviews/${this.company.slug}/reviews/${this.selectedReview.lead.pid}?pid=true&expand=lead`, { labels: value })
        this.updateReview(data)
        this.showMessage(['success', this.$t('success_message.successfully_updated')])
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.resetData()
      }
    },
    async deleteAssignee() {
      this.isDeletingAssignee = true
       try {
        const { data } = await this.$axios.patch(`/reviews/${this.company.slug}/reviews/${this.selectedReview.lead.pid}?pid=true&expand=lead`, { labels: [] })
        this.updateReview(data)
        this.showMessage(['success', this.$t('success_message.delete_successful')])
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.openDeleteAssigneeDialog = false
        this.resetData()
      }
    },
    updateReview(review) {
      const findIndex = this.reviews.findIndex((item) => item.id === review.id)
      if (findIndex >= 0) {
        this.reviews.splice(findIndex, 1, review)
        this.selectedReview = review
      }
    },
    resetData() {
      this.isShowingBottomView = false
      this.isSavingResponse = false
      this.isDeletingResponse = false
      this.isSavingAssignee = false
      this.isDeletingAssignee = false
    },
    selectDuration(duration) {
      this.selectedDuration = duration
      this.currentPage = 1
      this.fetchFeedback()
    },
    sortFeedback(option) {
      this.selectedSort = option.value
      this.currentPage = 1
      this.fetchFeedback()
    },
    assignTeam() {
      this.isEditingAssignee = true
      this.isShowingBottomView = true
    },
    selectReview(review) {
      this.resetData()
      this.selectedReview = review
      if (this.isMobileScreen) {
        this.openSelectedDialog = true
      }
    },
    infiniteScrolling(entries, observer, isIntersecting) {
      if (isIntersecting) {
        if (this.isLoading || !this.hasNext) {
          return
        }
        this.isLoadMore = true
        this.currentPage++
        this.fetchFeedback()
      }
    },
    getReviewText() {
      let text = ''
      if (this.selectedReview) {
        const review = this.selectedReview
        text = `Name: ${review.name}\nRating: ${review.star}/5\nDate: ${this.$options.filters.convertServerDate(review.created)}\n`
        if (review.comment) {
          text = text.concat(`Comment: ${review.comment}\n`)
        }
        if (review.extra_fields_data) {
          review.extra_fields_data.forEach(field => {
            if (field.type === 'address') {
              const { address_1, address_2, city, postcode, state } = field.value
              const address = `${address_1}, ${address_2}, ${city}, ${postcode}, ${state}`
              text = text.concat(`${field.name}: ${address}\n`)            
            } else {
              text = text.concat(`${field.name}: ${field.value}\n`)            
            }
          })
        }
      }
      return text
    },
    doneCopy() {
      this.showMessage(['success', this.$t('success_message.item_copied_to_clipboard', { item: this.$t('review') })])
    },
    hideSelectedDialog() {
      this.openSelectedDialog = false
    },
    downloadAllResponses() {
      this.isDownloading = true
      const params = {
        search: this.searchText,
        page_size: -1,
      }
      if (this.filters.length > 0) {
        this.filters.map(filter => {
          if (filter.value) {
            params[filter.key] = filter.value
          }
        })
      }
      if (this.selectedSort) {
        params['ordering'] = this.selectedSort === 'new' ? '-created' : 'created'
      }
      if (this.selectedDuration.value) {
        params['past_months'] = this.selectedDuration.value
      }
      let exportDomain = 'https://export-test.yezza.io'
      if (process.env.VUE_APP_ENV === 'prod') {
        exportDomain = 'https://export.yezza.io'
      }
      this.$axios.get(`${exportDomain}/reviews/${this.company.slug}/reviews/export_csv`, { params })
        .then(resp => {
          const url = window.URL.createObjectURL(new Blob([resp.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'feedback.csv');
          document.body.appendChild(link);
          link.click();
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isDownloading = false
        })
    },
    getLeftBtnText(review) {
      return review.lead ?
        this.$t('google_review_view_item_details', { item:this.os === 'review' ? this.$t('customer').toLowerCase() : this.$t('patient').toLowerCase() }) :
        this.$t('google_review_assign_item', { item:this.os === 'review' ? this.$t('customer').toLowerCase() : this.$t('patient').toLowerCase() })
    },
    viewCustomer(review) {
      this.$emit('showFeedbackDetails', review.lead)
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep svg.v-icon.notranslate.v-data-table-header__icon {
  height: 8px !important;
  width: 8px !important;
  margin-left: 8px;
}

.selected-button {
  background: #EEEEEE !important;
  border-radius: 14px;
}

.feedback-table {
  height: calc(100vh - 390px);
  overflow-y: auto;
  padding-bottom: 16px;
}

.feedback-column {
  height: calc(100vh - 300px);
  overflow-y: scroll;
  padding-bottom: 64px;
}

.download-button {
  position: sticky;
  left: 0;
  right: 0;
  bottom: 0;
}

.selected-row, .selected-row:hover {
  background: #F9FAFB !important;
}
</style>
